import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { 
    Image as ImageIcon, 
    Play, 
    X, 
    ChevronLeft, 
    ChevronRight,
    Camera
} from 'lucide-react';
import { Link } from 'react-router-dom';

const galleryItems = [
    {
        id: 1,
        type: 'video',
        thumbnail: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/zrs4fm4l_6.jpeg',
        src: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/q2m5080j_Concert%20Video1.mp4',
        alt: 'Live Concert Performance',
        category: 'Concert',
    },
    {
        id: 2,
        type: 'image',
        src: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/zrs4fm4l_6.jpeg',
        alt: 'The Carnival - Live Performance',
        category: 'Concert',
    },
    {
        id: 3,
        type: 'video',
        thumbnail: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/0tqq5lae_7.jpeg',
        src: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/e39qob32_Concert%20Video2.mp4',
        alt: 'Energetic Stage Performance',
        category: 'Concert',
    },
    {
        id: 4,
        type: 'image',
        src: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/wvv2ruft_16.jpeg',
        alt: 'Hawayein The Band - Full Team',
        category: 'Backstage',
    },
    {
        id: 5,
        type: 'video',
        thumbnail: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/ae7t0urh_13.jpeg',
        src: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/oonk1qwj_Concert%20Video%203.mp4',
        alt: 'Wedding Performance Highlights',
        category: 'Wedding',
    },
    {
        id: 6,
        type: 'image',
        src: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/rz7ko0bw_14.jpeg',
        alt: 'Colorful Stage Performance',
        category: 'Wedding',
    },
];

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1,
        },
    },
};

const itemVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.4 },
    },
};

export const GallerySection = () => {
    const [selectedItem, setSelectedItem] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);

    const openLightbox = (item, index) => {
        setSelectedItem(item);
        setCurrentIndex(index);
    };

    const closeLightbox = () => {
        setSelectedItem(null);
    };

    const navigateLightbox = (direction) => {
        const newIndex = direction === 'next' 
            ? (currentIndex + 1) % galleryItems.length 
            : (currentIndex - 1 + galleryItems.length) % galleryItems.length;
        setCurrentIndex(newIndex);
        setSelectedItem(galleryItems[newIndex]);
    };

    return (
        <section className="py-20 lg:py-28 relative overflow-hidden bg-card/30">
            {/* Background */}
            <div className="absolute inset-0 spotlight-amber pointer-events-none opacity-20" />

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-14 lg:mb-16"
                >
                    <div className="inline-flex items-center gap-2 mb-4">
                        <Camera className="w-5 h-5 text-primary" />
                        <span className="text-sm font-body font-medium text-primary uppercase tracking-wider">
                            Our Gallery
                        </span>
                    </div>
                    <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">
                        Moments That Matter
                    </h2>
                    <p className="text-muted-foreground text-base sm:text-lg font-body max-w-2xl mx-auto">
                        A glimpse into our world of live performances, backstage magic, and unforgettable events
                    </p>
                </motion.div>

                {/* Gallery Grid */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6"
                >
                    {galleryItems.map((item, index) => (
                        <motion.div
                            key={item.id}
                            variants={itemVariants}
                            className={`relative group cursor-pointer rounded-2xl overflow-hidden ${
                                index === 0 ? 'md:col-span-2 md:row-span-2' : ''
                            }`}
                            onClick={() => openLightbox(item, index)}
                        >
                            <div className={`relative ${index === 0 ? 'h-64 sm:h-80 md:h-full md:min-h-[400px]' : 'h-40 sm:h-52 lg:h-56'}`}>
                                <img
                                    src={item.type === 'video' ? item.thumbnail : item.src}
                                    alt={item.alt}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                                
                                {/* Overlay */}
                                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                
                                {/* Video Play Icon */}
                                {item.type === 'video' && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-14 h-14 rounded-full bg-primary/90 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 glow-primary">
                                            <Play className="w-6 h-6 text-foreground fill-foreground ml-1" />
                                        </div>
                                    </div>
                                )}

                                {/* Hover Content */}
                                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                                    <span className="inline-block px-3 py-1 text-xs font-body bg-primary/20 text-primary rounded-full mb-2">
                                        {item.category}
                                    </span>
                                    <p className="text-sm text-foreground font-body">{item.alt}</p>
                                </div>

                                {/* Border Effect */}
                                <div className="absolute inset-0 border border-primary/0 group-hover:border-primary/40 rounded-2xl transition-colors duration-300" />
                            </div>
                        </motion.div>
                    ))}
                </motion.div>

                {/* View All Button */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="text-center mt-10"
                >
                    <Link to="/gallery">
                        <Button 
                            variant="outline" 
                            className="border-border/50 bg-card/30 text-foreground font-heading font-semibold px-8 py-6 rounded-xl hover:bg-card/50 hover:border-primary/50"
                        >
                            <ImageIcon className="w-5 h-5 mr-2" />
                            View Full Gallery
                        </Button>
                    </Link>
                </motion.div>
            </div>

            {/* Lightbox Dialog */}
            <Dialog open={!!selectedItem} onOpenChange={closeLightbox}>
                <DialogContent className="max-w-4xl w-full bg-background/95 backdrop-blur-xl border-border/50 p-0 overflow-hidden">
                    <AnimatePresence mode="wait">
                        {selectedItem && (
                            <motion.div
                                key={selectedItem.id}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="relative"
                            >
                                {/* Close Button */}
                                <button
                                    onClick={closeLightbox}
                                    className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center text-foreground hover:bg-background transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>

                                {/* Navigation Arrows */}
                                <button
                                    onClick={() => navigateLightbox('prev')}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center text-foreground hover:bg-background transition-colors"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => navigateLightbox('next')}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center text-foreground hover:bg-background transition-colors"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>

                                {/* Content */}
                                <div className="aspect-video w-full">
                                    {selectedItem.type === 'video' ? (
                                        <video
                                            src={selectedItem.src}
                                            controls
                                            autoPlay
                                            className="w-full h-full bg-background"
                                        />
                                    ) : (
                                        <img
                                            src={selectedItem.src}
                                            alt={selectedItem.alt}
                                            className="w-full h-full object-contain bg-background"
                                        />
                                    )}
                                </div>

                                {/* Caption */}
                                <div className="p-4 bg-card/50">
                                    <span className="inline-block px-3 py-1 text-xs font-body bg-primary/20 text-primary rounded-full mb-2">
                                        {selectedItem.category}
                                    </span>
                                    <p className="text-foreground font-body">{selectedItem.alt}</p>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </DialogContent>
            </Dialog>
        </section>
    );
};
